import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import EditListsButton from './EditListsButton';
import { Paper, Typography, TableRow, TableHead, TableContainer, TableBody, Table, IconButton, Collapse, Box, Tooltip } from '@mui/material';
import { BadgeMinus, BadgeCheck, Pencil, ArrowDownAZ, ChevronDown, ChevronUp, PlusIcon, Trash2 } from 'lucide-react';
import FilterListRow from '../../../Shared/Tables/FilterListRow';
import { toast } from 'react-toastify';
import { dataService } from '../../../../Services/data/data.service';
import { crudTypes } from '../../../../Config/constants';
import DeleteConfirmDialog from './DeleteConfirmDialog';

function Row(props) {
    const classes = useStyles();
    const row = props.row;

    const [isExpanded, setIsExpanded] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, target: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const formatIsRevenue = (isRevenue) => {
        if (isRevenue) {
            return <p className={classes.chip__green}>Revenue</p>;
        } else {
            return <p className={classes.chip__red}>Expense</p>;
        }
    };

    const toggleDropdown = () => {
        setIsExpanded(!isExpanded);
    };

    const openDeleteCategoryDialog = () => {
        setDeleteDialog({ open: true, type: 'category', target: row });
    };

    const openDeleteAccountDialog = (account) => {
        setDeleteDialog({ open: true, type: 'account', target: account });
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({ open: false, type: null, target: null });
        setDeleteError(null);
    };

    const handleConfirmDelete = () => {
        setDeleteLoading(true);
        if (deleteDialog.type === 'category') {
            dataService.deleteAccountCategory(row.id, props.userId).then(response => {
                setDeleteLoading(false);
                if (response.success) {
                    closeDeleteDialog();
                    toast.success(response.message);
                    props.onAccountCategoryDeleteSuccess(row, crudTypes.DELETE);
                } else {
                    setDeleteError(response.message);
                }
            });
        } else {
            const account = deleteDialog.target;
            dataService.deleteAccount(account.id, props.userId).then(response => {
                setDeleteLoading(false);
                if (response.success) {
                    closeDeleteDialog();
                    toast.success(response.message);
                    const accountData = {
                        accountCategoryId: row.id,
                        companyId: row.companyId,
                        accounts: [account],
                    };
                    props.onAccountDeleteSuccess(accountData, crudTypes.DELETE);
                } else {
                    setDeleteError(response.message);
                }
            });
        }
    };

    const getDeleteDialogProps = () => {
        if (deleteDialog.type === 'category') {
            const accountCount = row.accounts ? row.accounts.length : 0;
            return {
                title: 'Delete Account Category',
                item: row.name,
                consequences: [
                    `Deactivate the account category "${row.name}"`,
                    ...(accountCount > 0
                        ? [`Deactivate all ${accountCount} account(s) under this category`]
                        : []),
                ],
            };
        } else if (deleteDialog.type === 'account') {
            return {
                title: 'Delete Account',
                item: deleteDialog.target.name,
                consequences: [
                    `Deactivate the account "${deleteDialog.target.name}" from category "${row.name}"`,
                ],
            };
        }
        return {};
    };

    return (
        <>
            {/* Main Row */}
            <tr className={classes.tableBodyRow} key={"thow"} style={props.index % 2 ? { background: "#e6ecf0" } : { background: "white" }}>
                <td className={classes.tableData}>
                    {row.name}
                </td>
                <td className={classes.tableData}>
                    {formatIsRevenue(row.isRevenue)}
                </td>
                <td>
                    <div className={classes.accountsCell}>
                        <p className={classes.accountsCell__count}>{row.accounts.length > 0 ? row.accounts.length + " account(s)" : "No accounts"}</p>
                        <IconButton onClick={toggleDropdown} size="large">
                            {isExpanded ? <ChevronUp /> : <ChevronDown />}
                        </IconButton>
                    </div>
                </td>
                <td className={classes.tableData} style={{ width: "50px", padding: "0px", textAlign: "center" }}>
                    {row.isActive ? <BadgeCheck style={{ color: "green" }} /> : <BadgeMinus style={{ color: "red" }} />}
                </td>
                <td>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <IconButton onClick={() => props.setAccountCategoryToEdit(row)} size="large" style={{ borderRadius: 0 }}>
                            <Pencil fontSize="inherit" />
                        </IconButton>
                        <IconButton onClick={openDeleteCategoryDialog} size="large" style={{ borderRadius: 0 }}>
                            <Trash2 fontSize="inherit" style={{ color: "#d32f2f" }} />
                        </IconButton>
                    </div>
                </td>
            </tr>
            {/* Dropdown Rows */}
            {isExpanded && row.accounts && row.accounts.map((account, idx) => (
                <tr className={classes.tableBodyRow} key={`account-${idx}`} style={{ background: "#f9f9f9" }}>
                    <td className={classes.tableData} colSpan={3}>
                        {account.name}
                    </td>
                    <td className={classes.tableData} style={{ width: "50px", padding: "0px", textAlign: "center" }}>
                        {account.isActive ? <BadgeCheck style={{ color: "green" }} /> : <BadgeMinus style={{ color: "red" }} />}
                    </td>
                    <td>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <IconButton
                                onClick={() => {
                                    const accountWithCompanyId = { ...account, companyId: row.companyId };
                                    props.setAccountToEdit(accountWithCompanyId);
                                }}
                                size="large"
                                style={{ borderRadius: 0 }}
                            >
                                <Pencil fontSize="inherit" />
                            </IconButton>
                            <IconButton onClick={() => openDeleteAccountDialog(account)} size="large" style={{ borderRadius: 0 }}>
                                <Trash2 fontSize="inherit" style={{ color: "#d32f2f" }} />
                            </IconButton>
                        </div>
                    </td>
                </tr>
            ))}

            <DeleteConfirmDialog
                open={deleteDialog.open}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
                loading={deleteLoading}
                error={deleteError}
                {...getDeleteDialogProps()}
            />
        </>
    );
}

export default function AccountList(props) {
    const classes = useStyles();

    return (
        <div>
            <Box className={classes.companyHeader}>
                <Typography variant="h5" className={classes.projectTitle}>
                    {props.companyName}
                </Typography>
                <button onClick={() => props.openCreateAccountDialog(props.companyId)} className={classes.iconButton}>
                    <PlusIcon className={classes.iconButton__icon} />
                </button>
            </Box>
            <TableContainer style={{ borderTopRightRadius: "8px", borderTopLeftRadius: '8px' }} component={Paper}>
                <Table size="sm" key={props.rows.id}>
                    <TableHead className={classes.headStyle} key={props.rows.id}>
                        <TableRow className={classes.tableBodyRow} key={props.rows.id}>
                            <th className={classes.tableHead} style={{ width: "320px" }} >
                                <span className={classes.titleSearch} style={{ paddingLeft: "8px", }}>
                                    <div className={classes.icons}>
                                        <Tooltip title="Alphabetical order" placement="top" arrow>
                                            <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting('name')}>
                                                <ArrowDownAZ />
                                            </IconButton>
                                        </Tooltip>
                                        Name
                                    </div>
                                </span>
                            </th>
                            <th className={classes.tableHead}>
                                <span className={classes.titleSearch}>
                                    <div className={classes.icons}>
                                        Revenue/Expense
                                    </div>
                                </span>
                            </th>
                            <th className={classes.tableHead}>
                                <span className={classes.titleSearch}>
                                    <div className={classes.icons}>
                                        Accounts
                                    </div>
                                </span>
                            </th>
                            <th className={classes.tableHead} style={{ width: "50px" }}>
                                <span className={classes.titleSearch}>
                                    <div className={classes.icons}>
                                        Active
                                    </div>
                                </span>
                            </th>
                            <th className={classes.tableHead} style={{ textAlign: 'center', width: "5%" }}>
                                Actions
                            </th>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.filterOn && <FilterListRow rows={props.rows} filters={props.filters} filterList={props.filterList} updateFilters={props.updateFilters}></FilterListRow>}
                        {props.rows.map((row, index) => (
                            <Row row={row} index={index} setAccountToEdit={props.setAccountToEdit} setAccountCategoryToEdit={props.setAccountCategoryToEdit} className={classes.tableBodyRow} userId={props.userId} onAccountCategoryDeleteSuccess={props.onAccountCategoryDeleteSuccess} onAccountDeleteSuccess={props.onAccountDeleteSuccess} />
                        ))}
                        {props.rows.length === 0 &&
                            <tr className={classes.tableBodyRow} >
                                <td colSpan={4}>No financial types available</td>
                            </tr>}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

const useStyles = makeStyles((theme) => ({
    companyHeader: {
        width: '100%',

        display: 'flex',
        justifyContent: 'space-between',
        alignItems: "center",

        padding: "16px",

        borderRadius: '8px 8px 0px 0px',

        backgroundColor: '#C3996B',
        color: 'white',
    },

    projectTitle: {
    },

    iconButton: {
        height: "32px",
        width: "32px",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "0C0E29",
        backgroundColor: "#F8F8F8",

        display: "flex",
        gap: "4px",
        alignItems: "center",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#EDEDED",
            cursor: "pointer",
        }
    },

    iconButton__icon: {
        height: "20px",
        width: "20px",

        display: "block",
    },

    tableHead: {
        height: '42px',
        textAlign: "left",
    },

    titleSearch: {
        position: 'relative',
        display: 'flex',
        padding: "0px 16px",
        justifyContent: 'flex-start',
        alignItems: 'center'
    },

    headStyle: {
        backgroundColor: "#e0dce4",
    },

    tableBodyRow: {
        justifyContent: "center",
        textAlign: "center",
    },

    tableRow: {
        borderBottom: '2px solid #e0dce4',
    },

    tableData: {
        padding: "8px 16px",
        textAlign: "left",
    },

    accountsCell: {
        width: "200px",

        display: 'flex',
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",

        paddingLeft: "16px",
    },

    accountsCell__count: {
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",

    },

    actionButtons: {
        padding: '0px !important',
        textAlign: "center",
        display: "flex",
        justifyContent: "center"
    },

    tableFooter: {
        width: '100%'
    },

    footer: {
        display: 'flex',
    },

    icons: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },

    chip__green: {
        width: "fit-content",
        height: "24px",

        display: "inline-flex",
        alignItems: "center",

        padding: "0px 12px",
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",

        borderRadius: "12px",

        backgroundColor: "#E2F8E0",
        color: "#3AB037"
    },

    chip__red: {
        width: "fit-content",
        height: "24px",

        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",

        padding: "0px 12px",
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",

        borderRadius: "12px",

        backgroundColor: "#FBE5E5",
        color: "#D84949"
    },
}));

